import {
    RequestStatus,
    ServerRequestState,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { Box, Button, MenuItem, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
    mandatoryFieldErrorMessage,
    tooManyCharsMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { SubmitButton } from '../ui/buttons/SubmitButton.tsx';
import { useNavigate } from 'react-router';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { RecodeCondition } from './CloudConnectorRecodeQrCodesAdd.tsx';
import { generateTimeOptions } from './CloudConnectorSmartCallTimeWindowConfig.tsx';
import { CloudConnectorRecodeConditionType } from 'dg-web-shared/model/RecodeCondition.ts';

const enum AutoRecodeFields {
    recodeConditionId = 'recodeConditionId',
    name = 'name',
    validFromDate = 'validFromDate',
    validFromTime = 'validFromTime',
    validToDate = 'validToDate',
    validToTime = 'validToTime',
}

export interface AutoRecodeFieldValues {
    [AutoRecodeFields.recodeConditionId]: string | null;
    [AutoRecodeFields.name]: string;
    [AutoRecodeFields.validFromDate]: DateTime | null;
    [AutoRecodeFields.validFromTime]: string | null;
    [AutoRecodeFields.validToDate]: DateTime | null;
    [AutoRecodeFields.validToTime]: string | null;
}

export function CloudConnectorAutoRecodeCreateEditForm({
    submitState,
    onSubmit,
    recodeConditionOptions,
    isRecodeAlreadyActive,
    defaultValues,
}: {
    submitState: ServerRequestState<unknown, ValidationData>;
    onSubmit: (formFieldValues: AutoRecodeFieldValues) => void;
    recodeConditionOptions: RecodeCondition[];
    isRecodeAlreadyActive: boolean;
    defaultValues?: AutoRecodeFieldValues;
}) {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const availableRecodeConditionOptions = recodeConditionOptions.filter(
        recodeCondition => {
            switch (recodeCondition.cloudConnectorRecodeConditionType) {
                case CloudConnectorRecodeConditionType.DEVICE_ONLY:
                case CloudConnectorRecodeConditionType.QR_AND_DEVICE:
                    return true;

                case CloudConnectorRecodeConditionType.QR_SELECTABLE_AMOUNT:
                case CloudConnectorRecodeConditionType.QR_SELECTABLE_TIME:
                    return false;
            }
        },
    );

    const { control, handleSubmit, watch } = useForm<AutoRecodeFieldValues>({
        defaultValues: defaultValues
            ? defaultValues
            : {
                  [AutoRecodeFields.recodeConditionId]:
                      availableRecodeConditionOptions.length == 1
                          ? availableRecodeConditionOptions[0].recodeConditionId
                          : null,
                  [AutoRecodeFields.name]: '',
                  [AutoRecodeFields.validFromDate]: null,
                  [AutoRecodeFields.validFromTime]: null,
                  [AutoRecodeFields.validToDate]: null,
                  [AutoRecodeFields.validToTime]: null,
              },
        mode: 'onBlur',
    });

    const timeOptions = generateTimeOptions('00:15', '23:45', 15);
    const validFromTimeOptions: string[] = ['00:00', ...timeOptions];
    const validToTimeOptions: string[] = [...timeOptions, '23:59'];
    // eslint-disable-next-line react-hooks/incompatible-library
    const validFromDate = watch(AutoRecodeFields.validFromDate);

    const maxLengthDescription = 50;
    const descriptionTooLong = useTranslation(
        tooManyCharsMessage(maxLengthDescription),
    );
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const savePending = submitState.status == RequestStatus.PENDING;
    return (
        <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
                padding: '25px',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
            }}
        >
            <BackendRequestErrorMessage requestState={submitState} />
            <ReactHookFormTextField
                readonly={
                    availableRecodeConditionOptions.length <= 1 ||
                    isRecodeAlreadyActive
                }
                label={
                    <Localized
                        de="Rabattbedingung"
                        fr="Condition de rabais"
                        it="Condizione ribasso"
                        en="Discount condition"
                    />
                }
                name={AutoRecodeFields.recodeConditionId}
                control={control}
                select
                rules={{
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                }}
            >
                {availableRecodeConditionOptions
                    .sort((a, b) =>
                        a.description[language].localeCompare(
                            b.description[language],
                        ),
                    )
                    .map(recodeCondition => (
                        <MenuItem
                            key={recodeCondition.recodeConditionId}
                            value={recodeCondition.recodeConditionId}
                        >
                            <Localized {...recodeCondition.description} />
                        </MenuItem>
                    ))}
            </ReactHookFormTextField>
            <ReactHookFormTextField
                label={<Localized de="Name" fr="Nom" it="Nome" en="Name" />}
                name={AutoRecodeFields.name}
                control={control}
                rules={{
                    required: {
                        value: true,
                        message: mandatoryFieldMessage,
                    },
                    maxLength: {
                        value: maxLengthDescription,
                        message: descriptionTooLong,
                    },
                    minLength: {
                        value: 1,
                        message: mandatoryFieldMessage,
                    },
                }}
            />
            <Stack
                direction="row"
                spacing={2}
                sx={{
                    alignItems: 'baseline',
                }}
            >
                <Controller
                    name={AutoRecodeFields.validFromDate}
                    control={control}
                    render={({ field, fieldState }) => (
                        <DesktopDatePicker
                            readOnly={isRecodeAlreadyActive}
                            format="dd.MM.yyyy"
                            label={
                                <Localized
                                    de="Beginn"
                                    fr="Début"
                                    it="Inizio"
                                    en="Start"
                                />
                            }
                            value={field.value}
                            onChange={field.onChange}
                            minDate={DateTime.now()}
                            maxDate={DateTime.now().plus({ year: 1 })}
                            slotProps={{
                                textField: {
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message,
                                },
                            }}
                        />
                    )}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                />
                <ReactHookFormTextField
                    readonly={isRecodeAlreadyActive}
                    control={control}
                    name={AutoRecodeFields.validFromTime}
                    select
                    sx={{ width: '75px' }}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                >
                    {validFromTimeOptions.map(time => (
                        <MenuItem key={time} value={time}>
                            {time}
                        </MenuItem>
                    ))}
                </ReactHookFormTextField>
            </Stack>
            <Stack
                direction="row"
                spacing={2}
                sx={{
                    alignItems: 'baseline',
                }}
            >
                <Controller
                    name={AutoRecodeFields.validToDate}
                    control={control}
                    render={({ field, fieldState }) => (
                        <DesktopDatePicker
                            format="dd.MM.yyyy"
                            label={
                                <Localized
                                    de="Ende"
                                    fr="Fin"
                                    it="Fine"
                                    en="End"
                                />
                            }
                            value={field.value}
                            onChange={field.onChange}
                            minDate={
                                validFromDate ? validFromDate : DateTime.now()
                            }
                            maxDate={
                                validFromDate
                                    ? validFromDate.plus({ months: 1 })
                                    : DateTime.now().plus({
                                          years: 1,
                                          months: 1,
                                      })
                            }
                            slotProps={{
                                textField: {
                                    error: !!fieldState.error,
                                    helperText: fieldState.error?.message,
                                },
                            }}
                        />
                    )}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                />
                <ReactHookFormTextField
                    control={control}
                    name={AutoRecodeFields.validToTime}
                    select
                    sx={{ width: '75px' }}
                    rules={{
                        required: {
                            value: true,
                            message: mandatoryFieldMessage,
                        },
                    }}
                >
                    {validToTimeOptions.map(time => (
                        <MenuItem key={time} value={time}>
                            {time}
                        </MenuItem>
                    ))}
                </ReactHookFormTextField>
            </Stack>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingTop: '8px',
                }}
            >
                <Button
                    onClick={() => navigate('..')}
                    variant="outlined"
                    disabled={savePending}
                >
                    <Localized
                        de="Abbrechen"
                        fr="Annuler"
                        it="Annulla"
                        en="Cancel"
                    />
                </Button>
                <SubmitButton
                    sx={{ marginLeft: '5px' }}
                    loading={savePending}
                    label={
                        <Localized
                            de="Speichern"
                            fr="Sauvegarder"
                            it="Salva"
                            en="Save"
                        />
                    }
                />
            </Box>
        </Box>
    );
}
